import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faPlay,
  faCheck,
  faXmark,
  faChevronDown,
  faTriangleExclamation,
  faDownload,
  faEye,
  faArrowDownWideShort,
  faArrowUpWideShort,
  faArrowUpRightFromSquare,
  faTrash,
  faListCheck,
  faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import {
  DeployTest,
  DeployTestActionItem,
  DeployTestItemStatus,
  DeployTestLibraryItem,
  DeployTestQuestionType,
  DeployTestStatus,
} from '../../types';
import { API_BASE_URL } from '../../config/api';
import { markdownToHtml } from '../../utils/markdownHtml';
import { BETA_PROGRAM, DeployProgram } from '../../config/deployProgram';
import { downloadDeployTestPdf } from '../../utils/deployTestPdf';
import {
  STATUS_OPTIONS,
  ITEM_STATUS_OPTIONS,
  canStartDeployTest,
  firmwareTypeLabel,
  hasRequiredVersions,
  hasOutstandingLinearItems,
  planningBlockers,
  resolveItemStatus,
  statusMeta,
} from './deployTestUtils';
import DeployTestActionItems, {
  ActionItemCreatePreset,
  descriptionFromTask,
} from './DeployTestActionItems';
import DeployTestTicketTabs from './DeployTestTicketTabs';

interface BetaTestingTabProps {
  program?: DeployProgram;
}

type LibraryManageKind = 'task' | 'question';

const testCompletionPercent = (test: DeployTest) => {
  const total = test.totalReportCount || test.betaUserCount || test.testerCount || 0;
  if (!total) return 0;
  return Math.round(((test.completedReportCount || 0) / total) * 100);
};

const MiniResultPie = ({
  value,
  total,
  label,
  detail,
}: {
  value: number;
  total: number;
  label: string;
  detail?: string;
}) => {
  const percent = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <span className="deploy-test-mini-pie-row">
      <span
        className="deploy-test-mini-pie"
        style={{ background: `conic-gradient(#2563eb ${percent}%, #e5e7eb 0)` }}
        title={`${percent}%`}
        aria-hidden="true"
      />
      <span>
        <strong>{value} of {total}</strong>
        {` ${label}`}
        {detail ? <em> {detail}</em> : null}
      </span>
    </span>
  );
};

const formatPreviewVersion = (value?: string) => {
  const version = String(value || '').trim().replace(/^v/i, '');
  return version ? `v${version}` : 'Not specified';
};

const stripMarkdownListMarker = (value: string) => value.trim().replace(/^([-*+]\s+|\d+[.)]\s+)/, '').trim();

const normalizeFilterVersion = (value?: string) => String(value || '').trim().replace(/^v/i, '');

const uniqueVersions = (values: Array<string | undefined>) => (
  [...new Set(values.map(normalizeFilterVersion).filter(Boolean))]
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
);

const hasOutstandingActionItems = (test: DeployTest) => (
  hasOutstandingLinearItems(test.actionItems)
);

const STATUS_SORT_ORDER: Record<DeployTestStatus, number> = {
  planning: 0,
  testing: 1,
  succeeded: 2,
  aborted: 3,
  failed: 4,
};

const testCreatedAt = (test: DeployTest) => {
  const time = test.createdAt ? new Date(test.createdAt).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
};

const compareTests = (left: DeployTest, right: DeployTest, sortBy: 'date' | 'status', flipped: boolean) => {
  const cmp = sortBy === 'status'
    ? (STATUS_SORT_ORDER[left.status] - STATUS_SORT_ORDER[right.status])
      || (testCreatedAt(right) - testCreatedAt(left))
    : testCreatedAt(right) - testCreatedAt(left);
  return flipped ? -cmp : cmp;
};

const testMatchesAppVersion = (test: DeployTest, filter: string) => {
  if (!filter) return true;
  const wanted = normalizeFilterVersion(filter);
  return [
    test.mobileAppVersion,
    test.mobileAppVersionIos,
    test.mobileAppVersionAndroid,
  ].some((version) => normalizeFilterVersion(version) === wanted);
};

const buildKickoffEmailPreview = (test: DeployTest, program: DeployProgram) => {
  const testName = test.name?.trim() || 'Not specified';
  const testType = program.track === 'internal' ? 'Internal Test' : 'Beta Test';
  const releaseNotes = String(test.releaseNotes || '')
    .split('\n')
    .map(stripMarkdownListMarker)
    .filter(Boolean);
  const tasks = (test.items || [])
    .map((item) => {
      const title = item.text.trim();
      const description = (item.description || '').trim();
      if (!title && !description) return null;
      return {
        title: title || 'Untitled task',
        description,
      };
    })
    .filter(Boolean) as { title: string; description: string }[];

  return {
    subject: `New ${testType} Ready (${testName})`,
    subjectPrefix: `New ${testType} Ready`,
    testName,
    body: test.kickoffEmail?.trim() || '[Email intro]',
    firmwareVersion: formatPreviewVersion(test.firmwareVersion),
    mobileAppVersion: formatPreviewVersion(test.mobileAppVersion),
    mobileAppVersionIos: formatPreviewVersion(test.mobileAppVersionIos || test.mobileAppVersion),
    mobileAppVersionAndroid: formatPreviewVersion(test.mobileAppVersionAndroid || test.mobileAppVersion),
    releaseNotes,
    instructions: test.overview?.trim() || '[Instructions]',
    tasks,
    wikiUrl: 'https://wiki.fluoraplant.com/beta',
  };
};

const BetaTestingTab: React.FC<BetaTestingTabProps> = ({ program = BETA_PROGRAM }) => {
  const [tests, setTests] = useState<DeployTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilters, setStatusFilters] = useState<DeployTestStatus[]>([]);
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [firmwareFilter, setFirmwareFilter] = useState('');
  const [mobileAppFilter, setMobileAppFilter] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'status'>('date');
  const [sortFlipped, setSortFlipped] = useState(false);
  const statusDropdownRef = useRef<HTMLDetailsElement | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [startEmailTest, setStartEmailTest] = useState<DeployTest | null>(null);
  const [startModalError, setStartModalError] = useState<string | null>(null);
  const [startAction, setStartAction] = useState<'without-email' | 'with-email' | null>(null);
  const [libraryManageKind, setLibraryManageKind] = useState<LibraryManageKind | null>(null);
  const [libraryItems, setLibraryItems] = useState<DeployTestLibraryItem[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [savingLibraryItemId, setSavingLibraryItemId] = useState<string | null>(null);
  const [deletingLibraryItemId, setDeletingLibraryItemId] = useState<string | null>(null);
  const [actionItemPreset, setActionItemPreset] = useState<(ActionItemCreatePreset & { testId: string }) | null>(null);

  const updateActionItems = useCallback((testId: string, actionItems: DeployTestActionItem[]) => {
    setTests((current) => current.map((test) => (
      test._id === testId ? { ...test, actionItems } : test
    )));
  }, []);

  const updateTodoItems = useCallback((testId: string, todoItems: DeployTestActionItem[]) => {
    setTests((current) => current.map((test) => (
      test._id === testId ? { ...test, todoItems } : test
    )));
  }, []);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { track: program.track, limit: 100, page: 1 };
      const response = await axios.get(`${API_BASE_URL}${program.testsApi}`, {
        withCredentials: true,
        params,
      });
      setTests(response.data.tests || []);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching deploy tests:', err);
      setError(err.response?.data?.message || 'Failed to load deploy tests');
    } finally {
      setLoading(false);
    }
  }, [program.testsApi, program.track]);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  useEffect(() => {
    const closeStatusDropdown = (event: MouseEvent) => {
      const dropdown = statusDropdownRef.current;
      if (!dropdown?.open) return;
      if (event.target instanceof Node && dropdown.contains(event.target)) return;
      dropdown.open = false;
    };
    document.addEventListener('pointerdown', closeStatusDropdown);
    return () => document.removeEventListener('pointerdown', closeStatusDropdown);
  }, []);

  const loadLibraryItems = useCallback(async (kind: LibraryManageKind) => {
    setLibraryLoading(true);
    setLibraryError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}${program.testsApi}/library`, {
        withCredentials: true,
        params: { itemType: kind },
      });
      setLibraryItems(response.data.items || []);
    } catch (err: any) {
      setLibraryError(err.response?.data?.message || 'Failed to load library items');
    } finally {
      setLibraryLoading(false);
    }
  }, [program.testsApi]);

  const openLibraryManager = (kind: LibraryManageKind) => {
    setLibraryManageKind(kind);
    setLibraryItems([]);
    loadLibraryItems(kind);
  };

  const closeLibraryManager = () => {
    if (savingLibraryItemId || deletingLibraryItemId) return;
    setLibraryManageKind(null);
    setLibraryError(null);
  };

  const updateLibraryDraft = (itemId: string, patch: Partial<DeployTestLibraryItem>) => {
    setLibraryItems((prev) => prev.map((item) => (
      item._id === itemId ? { ...item, ...patch } : item
    )));
  };

  const saveLibraryItem = async (item: DeployTestLibraryItem) => {
    setSavingLibraryItemId(item._id);
    setLibraryError(null);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}${program.testsApi}/library/${item._id}`,
        item.itemType === 'task'
          ? {
              itemType: 'task',
              text: item.text || '',
              description: item.description || '',
            }
          : {
              itemType: 'question',
              prompt: item.prompt || '',
              type: item.type || 'text',
              additionalInfo: item.additionalInfo || '',
              choices: item.type === 'choice' ? (item.choices || []) : [],
            },
        { withCredentials: true }
      );
      const saved = response.data.item as DeployTestLibraryItem;
      setLibraryItems((prev) => prev.map((entry) => (entry._id === saved._id ? saved : entry)));
    } catch (err: any) {
      setLibraryError(err.response?.data?.message || 'Failed to save library item');
    } finally {
      setSavingLibraryItemId(null);
    }
  };

  const deleteLibraryItem = async (item: DeployTestLibraryItem) => {
    const label = item.itemType === 'task' ? item.text : item.prompt;
    if (!window.confirm(`Delete "${label || 'Untitled item'}" from the library?`)) return;

    setDeletingLibraryItemId(item._id);
    setLibraryError(null);
    try {
      await axios.delete(`${API_BASE_URL}${program.testsApi}/library/${item._id}`, {
        withCredentials: true,
      });
      setLibraryItems((prev) => prev.filter((entry) => entry._id !== item._id));
    } catch (err: any) {
      setLibraryError(err.response?.data?.message || 'Failed to delete library item');
    } finally {
      setDeletingLibraryItemId(null);
    }
  };

  const handleTestStatusChange = async (test: DeployTest, status: DeployTestStatus) => {
    if (
      program.track === 'internal'
      && test.status === 'planning'
      && status !== 'planning'
      && hasOutstandingLinearItems(test.todoItems)
    ) {
      const message = 'Complete outstanding TODOs before leaving Planning';
      setError(message);
      return { ok: false, message };
    }
    if (status === 'testing' && !canStartDeployTest(test)) {
      const message = (
        hasRequiredVersions(test)
          ? program.startBlockedTitle
          : 'Select a firmware version, iOS app version, and Android app version before starting the test.'
      );
      setError(message);
      return { ok: false, message };
    }
    setUpdatingStatusId(test._id);
    setError(null);
    setTests((prev) =>
      prev.map((entry) => (entry._id === test._id ? { ...entry, status } : entry))
    );

    try {
      await axios.patch(
        `${API_BASE_URL}${program.testsApi}/${test._id}`,
        { status },
        { withCredentials: true }
      );
      return { ok: true };
    } catch (err: any) {
      console.error('Error updating test status:', err);
      const message = err.response?.data?.message || 'Failed to update test status';
      setError(message);
      fetchTests();
      return { ok: false, message };
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const openStartTestModal = (test: DeployTest) => {
    setStartEmailTest(test);
    setStartModalError(null);
  };

  const closeStartTestModal = () => {
    if (startAction) return;
    setStartEmailTest(null);
    setStartModalError(null);
  };

  const startTestFromModal = async (sendEmail: boolean) => {
    if (!startEmailTest || startAction) return;
    const preview = buildKickoffEmailPreview(startEmailTest, program);

    setStartAction(sendEmail ? 'with-email' : 'without-email');
    setStartModalError(null);

    const started = await handleTestStatusChange(startEmailTest, 'testing');
    if (!started.ok) {
      setStartAction(null);
      setStartModalError(started.message || 'Could not start the test.');
      return;
    }

    if (!sendEmail) {
      setStartEmailTest(null);
      setStartAction(null);
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}${program.testsApi}/${startEmailTest._id}/send-kickoff-email`,
        {
          recipients: [],
          subject: preview.subject,
          intro: startEmailTest.kickoffEmail || '',
          testName: startEmailTest.name,
          firmwareVersion: startEmailTest.firmwareVersion,
          mobileAppVersion: startEmailTest.mobileAppVersion,
          mobileAppVersionIos: startEmailTest.mobileAppVersionIos || startEmailTest.mobileAppVersion,
          mobileAppVersionAndroid: startEmailTest.mobileAppVersionAndroid || startEmailTest.mobileAppVersion,
          releaseNotes: preview.releaseNotes,
          instructions: startEmailTest.overview || '',
          tasks: preview.tasks,
          wikiUrl: preview.wikiUrl,
        },
        { withCredentials: true }
      );
      setStartEmailTest(null);
    } catch (err: any) {
      setStartModalError(err.response?.data?.message || 'Test started, but the kickoff email failed to send.');
      fetchTests();
    } finally {
      setStartAction(null);
    }
  };

  const handleCardItemStatusChange = async (
    test: DeployTest,
    itemIndex: number,
    status: DeployTestItemStatus
  ) => {
    if (test.status !== 'testing') return;

    const items = (test.items || []).map((item, index) => ({
      _id: item._id,
      text: item.text,
      description: item.description || '',
      status: index === itemIndex ? status : resolveItemStatus(item),
    }));

    setTests((prev) =>
      prev.map((entry) => (
        entry._id === test._id
          ? { ...entry, items: entry.items.map((item, index) => (
              index === itemIndex ? { ...item, status } : item
            )) }
          : entry
      ))
    );

    try {
      await axios.patch(
        `${API_BASE_URL}${program.testsApi}/${test._id}`,
        { items },
        { withCredentials: true }
      );
    } catch (err: any) {
      console.error('Error updating task status:', err);
      setError(err.response?.data?.message || 'Failed to update task status');
      fetchTests();
    }
  };

  const handleDownloadPdf = async (test: DeployTest) => {
    setDownloadingId(test._id);
    try {
      const [testResponse, reportsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}${program.testsApi}/${test._id}`, {
          withCredentials: true,
        }),
        test.status === 'succeeded' || test.status === 'failed'
          ? axios.get(`${API_BASE_URL}${program.testsApi}/${test._id}/reports`, {
              withCredentials: true,
            })
          : Promise.resolve({ data: { reports: [] } }),
      ]);
      downloadDeployTestPdf(testResponse.data.test || test, reportsResponse.data.reports || []);
    } catch (err) {
      console.error('Error downloading deploy test PDF:', err);
      downloadDeployTestPdf(test);
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (value?: string | Date) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  const startEmailPreview = startEmailTest ? buildKickoffEmailPreview(startEmailTest, program) : null;
  const defaultKickoffAudienceLabel = program.track === 'internal' ? 'all internal testers' : 'all beta testers';
  const firmwareVersions = uniqueVersions(tests.map((test) => test.firmwareVersion));
  const mobileAppVersions = uniqueVersions(tests.flatMap((test) => [
    test.mobileAppVersion,
    test.mobileAppVersionIos,
    test.mobileAppVersionAndroid,
  ]));
  const visibleTests = tests.filter((test) => {
    if (statusFilters.length && !statusFilters.includes(test.status)) return false;
    if (outstandingOnly && !hasOutstandingActionItems(test)) return false;
    if (firmwareFilter && normalizeFilterVersion(test.firmwareVersion) !== firmwareFilter) return false;
    if (!testMatchesAppVersion(test, mobileAppFilter)) return false;
    return true;
  }).sort((left, right) => compareTests(left, right, sortBy, sortFlipped));

  const toggleStatusFilter = (status: DeployTestStatus) => {
    setStatusFilters((current) => (
      current.includes(status)
        ? current.filter((value) => value !== status)
        : [...current, status]
    ));
  };

  return (
    <>
      <div className="deploy-test-filters mb-3">
          <div className="deploy-test-filter-row">
          <div className="deploy-test-version-filters">
            <div className="deploy-test-version-filter">
              <span>Status</span>
              <details className="deploy-test-status-dropdown" ref={statusDropdownRef}>
                <summary>
                  {statusFilters.length
                    ? STATUS_OPTIONS
                      .filter((option) => statusFilters.includes(option.value))
                      .map((option) => option.label)
                      .join(', ')
                    : 'All'}
                </summary>
                <div className="deploy-test-status-menu" role="group" aria-label="Filter tests by status">
                  {STATUS_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`deploy-test-status-check${statusFilters.includes(option.value) ? ' is-on' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={statusFilters.includes(option.value)}
                        onChange={() => toggleStatusFilter(option.value)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </details>
            </div>
            <label className="deploy-test-version-filter">
              <span>Firmware</span>
              <select
                className="form-select form-select-sm"
                value={firmwareFilter}
                onChange={(event) => setFirmwareFilter(event.target.value)}
                aria-label="Filter tests by firmware version"
              >
                <option value="">All</option>
                {firmwareVersions.map((version) => (
                  <option key={version} value={version}>v{version}</option>
                ))}
              </select>
            </label>
            <label className="deploy-test-version-filter">
              <span>Mobile App</span>
              <select
                className="form-select form-select-sm"
                value={mobileAppFilter}
                onChange={(event) => setMobileAppFilter(event.target.value)}
                aria-label="Filter tests by mobile app version"
              >
                <option value="">All</option>
                {mobileAppVersions.map((version) => (
                  <option key={version} value={version}>v{version}</option>
                ))}
              </select>
            </label>
            <label className="deploy-test-version-filter">
              <span>Order</span>
              <select
                className="form-select form-select-sm"
                value={sortBy}
                onChange={(event) => {
                  setSortBy(event.target.value as 'date' | 'status');
                  setSortFlipped(false);
                }}
                aria-label="Order tests"
              >
                <option value="date">Date</option>
                <option value="status">Status</option>
              </select>
              <button
                type="button"
                className={`btn btn-sm ${sortFlipped ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => setSortFlipped((current) => !current)}
                title={sortBy === 'status'
                  ? (sortFlipped ? 'Failed first' : 'Planning first')
                  : (sortFlipped ? 'Oldest first' : 'Newest first')}
                aria-label="Flip sort order"
              >
                <FontAwesomeIcon icon={sortFlipped ? faArrowUpWideShort : faArrowDownWideShort} />
                Flip
              </button>
            </label>
          </div>
          <div className="deploy-test-filter-actions">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => openLibraryManager('task')}
            >
              <FontAwesomeIcon icon={faListCheck} className="me-1" />
              Manage Tasks
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => openLibraryManager('question')}
            >
              <FontAwesomeIcon icon={faCircleQuestion} className="me-1" />
              Manage Questions
            </button>
            <Link to={program.testPath('new')} className="btn btn-primary btn-sm">
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              New Test
            </Link>
          </div>
          </div>
          <label className="deploy-test-outstanding-check">
            <input
              type="checkbox"
              checked={outstandingOnly}
              onChange={(event) => setOutstandingOnly(event.target.checked)}
            />
            Show outstanding Action Items
          </label>
        </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close" />
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : visibleTests.length > 0 ? (
        <div className="deploy-test-list">
          {visibleTests.map((test) => {
            const items = test.items || [];
            const passedCount = items.filter((item) => resolveItemStatus(item) === 'passed').length;
            const totalCount = items.length;
            const percentComplete = totalCount > 0 ? Math.round((passedCount / totalCount) * 100) : 0;
            const canEditTaskStatus = test.status === 'testing';
            const outstandingTodos = program.track === 'internal' && hasOutstandingLinearItems(test.todoItems);
            const canStartTest = canStartDeployTest(test) && !outstandingTodos;
            const firmwareLabel = firmwareTypeLabel(test.firmwareType);
            const blockers = test.status === 'planning' ? [
              ...planningBlockers(test, program.firmwareNotReady),
              ...(outstandingTodos ? ['Outstanding TODOs must be completed first'] : []),
            ] : [];
            const meta = statusMeta(test.status);
            return (
              <div key={test._id} className="deploy-test-card">
                <div className={`deploy-test-card-status ${test.status}`}>
                  {meta.label}
                </div>
                <div className="deploy-test-card-body">
                  <div className="deploy-test-card-main min-w-0">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div className="min-w-0">
                        <h3 className="deploy-test-card-title">{test.name}</h3>
                        <div className="deploy-test-card-date">{formatDate(test.createdAt)}</div>
                      </div>
                      <div className="d-flex gap-2 flex-shrink-0">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => handleDownloadPdf(test)}
                          disabled={downloadingId === test._id}
                          title="Download test PDF"
                        >
                          {downloadingId === test._id ? (
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                          ) : (
                            <FontAwesomeIcon icon={faDownload} className="me-1" />
                          )}
                          Download
                        </button>
                        <a
                          href={program.publicTestHref(test)}
                          className="btn btn-outline-secondary btn-sm"
                          title="View public test page"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FontAwesomeIcon icon={faEye} className="me-1" />
                          View
                        </a>
                        <Link
                          to={program.testPath(test._id)}
                          className="btn btn-outline-primary btn-sm"
                          title={test.status === 'planning' ? 'Edit test' : 'Open test'}
                        >
                          <FontAwesomeIcon icon={faPen} className="me-1" />
                          Edit
                        </Link>
                      </div>
                    </div>
                    <div className="deploy-test-card-versions">
                      <div>
                        <span className="deploy-test-card-version-label">Firmware</span>
                        <span className="deploy-test-card-version-value">
                          {test.firmwareVersion ? (
                            `v${test.firmwareVersion}`
                          ) : (
                            <span className="deploy-test-select-later">Select Later</span>
                          )}
                          {firmwareLabel && (
                            <span className={`deploy-test-fw-badge is-${test.firmwareType}`}>{firmwareLabel}</span>
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="deploy-test-card-version-label">Mobile App (iOS)</span>
                        <span className="deploy-test-card-version-value">
                          {test.mobileAppVersionIos || test.mobileAppVersion ? (
                            `v${test.mobileAppVersionIos || test.mobileAppVersion}`
                          ) : (
                            <span className="deploy-test-select-later">Select Later</span>
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="deploy-test-card-version-label">Mobile App (Android)</span>
                        <span className="deploy-test-card-version-value">
                          {test.mobileAppVersionAndroid || test.mobileAppVersion ? (
                            `v${test.mobileAppVersionAndroid || test.mobileAppVersion}`
                          ) : (
                            <span className="deploy-test-select-later">Select Later</span>
                          )}
                        </span>
                      </div>
                    </div>
                    {blockers.length > 0 && (
                      <div className="deploy-test-card-warning">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="deploy-test-card-warning-icon" />
                        <div>
                          <div className="deploy-test-card-warning-title">Not ready to start testing</div>
                          <ul>
                            {blockers.map((blocker) => (
                              <li key={blocker}>{blocker}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    {test.overview?.trim() && (
                      <div className="deploy-test-card-instructions">
                        <span className="deploy-test-card-version-label">Instructions</span>
                        <p>{test.overview}</p>
                      </div>
                    )}
                    <div className="deploy-test-card-sections">
                    <div className="deploy-test-card-tasks-box">
                      <div className="deploy-test-card-meta">
                        <span>
                          <strong>Tasks</strong>: {passedCount} of {totalCount} complete ({percentComplete}%)
                        </span>
                      </div>
                      {totalCount > 0 ? (
                        <ul className="deploy-test-card-tasks">
                          {items.map((item, index) => {
                            const itemStatus = test.status === 'planning'
                              ? 'planning'
                              : test.status === 'aborted'
                                ? 'aborted'
                                : resolveItemStatus(item);
                            return (
                              <li
                                key={item._id || `${test._id}-${index}`}
                                className={`is-${itemStatus}`}
                              >
                                {test.status === 'planning' || test.status === 'aborted' ? (
                                  <span className={`deploy-test-card-task-status is-${itemStatus}`}>
                                    {test.status === 'aborted' ? 'Aborted' : 'Planning'}
                                  </span>
                                ) : (
                                  <span className={`deploy-test-card-task-status-wrap${canEditTaskStatus ? ' is-editable' : ''}`}>
                                    <select
                                      className={`deploy-test-card-task-status is-${itemStatus}`}
                                      value={itemStatus}
                                      disabled={!canEditTaskStatus}
                                      onChange={(e) => handleCardItemStatusChange(
                                        test,
                                        index,
                                        e.target.value as DeployTestItemStatus
                                      )}
                                      aria-label={`${item.text} status`}
                                    >
                                      {ITEM_STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                    {canEditTaskStatus && (
                                      <FontAwesomeIcon
                                        icon={faChevronDown}
                                        className="deploy-test-card-task-caret"
                                        aria-hidden="true"
                                      />
                                    )}
                                  </span>
                                )}
                                <span className="deploy-test-card-task-title">{item.text}</span>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm deploy-test-card-task-action"
                                  onClick={() => setActionItemPreset({
                                    testId: test._id,
                                    title: item.text.trim() || 'Untitled task',
                                    description: descriptionFromTask(test, program, {
                                      text: item.text,
                                      description: item.description,
                                      status: itemStatus,
                                    }),
                                    nonce: Date.now(),
                                  })}
                                >
                                  Create Action Item
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="deploy-test-card-tasks-empty">No tasks yet</div>
                      )}
                    </div>
                    <div className="deploy-test-card-tasks-box">
                      <div className="deploy-test-card-meta">
                        <span>
                          <strong>Results</strong>
                        </span>
                      </div>
                      <div className="deploy-test-card-reports">
                        <MiniResultPie
                          value={test.completedReportCount || 0}
                          total={test.totalReportCount || test.betaUserCount || test.testerCount || 0}
                          label="tests completed"
                          detail={`(${testCompletionPercent(test)}% complete)`}
                        />
                        <MiniResultPie
                          value={test.completedTesterCount || 0}
                          total={test.testerCount || 0}
                          label="testers completed testing"
                        />
                        {(!test.mobileAppPlatforms?.length || test.mobileAppPlatforms.includes('ios')) && (
                          <span className="deploy-test-card-reports-platform">
                            <strong>{test.iosCompletedReportCount || 0}</strong>
                            {` iOS test${(test.iosCompletedReportCount || 0) === 1 ? '' : 's'} received`}
                          </span>
                        )}
                        {(!test.mobileAppPlatforms?.length || test.mobileAppPlatforms.includes('android')) && (
                          <span className="deploy-test-card-reports-platform">
                            <strong>{test.androidCompletedReportCount || 0}</strong>
                            {` Android test${(test.androidCompletedReportCount || 0) === 1 ? '' : 's'} received`}
                          </span>
                        )}
                        <Link
                          to={program.resultsPath(test._id)}
                          className="btn btn-primary btn-sm"
                        >
                          See Results
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-2" />
                        </Link>
                      </div>
                    </div>
                    {program.track === 'internal' ? (
                      <DeployTestTicketTabs
                        test={test}
                        program={program}
                        onActionItemsChange={updateActionItems}
                        onTodoItemsChange={updateTodoItems}
                        createPreset={actionItemPreset?.testId === test._id ? actionItemPreset : null}
                        fixTestingOptions={tests}
                      />
                    ) : (
                      <DeployTestActionItems
                        test={test}
                        program={program}
                        onActionItemsChange={updateActionItems}
                        createPreset={actionItemPreset?.testId === test._id ? actionItemPreset : null}
                        fixTestingOptions={tests}
                      />
                    )}
                    </div>
                    {(test.status === 'planning' || test.status === 'testing') && (
                      <div className="deploy-test-card-actions">
                        {test.status === 'planning' && (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => openStartTestModal(test)}
                            disabled={updatingStatusId === test._id || !canStartTest}
                            title={
                              canStartTest
                                ? 'Start test'
                                : outstandingTodos
                                  ? 'Complete outstanding TODOs before starting the test'
                                  : hasRequiredVersions(test)
                                    ? program.startBlockedTitle
                                    : 'Select firmware and mobile app versions first'
                            }
                          >
                            <FontAwesomeIcon icon={faPlay} className="me-1" />
                            Start Test
                          </button>
                        )}
                        {test.status === 'testing' && (
                          <>
                            <button
                              type="button"
                              className="btn btn-success btn-sm"
                              onClick={() => handleTestStatusChange(test, 'succeeded')}
                              disabled={updatingStatusId === test._id}
                            >
                              <FontAwesomeIcon icon={faCheck} className="me-1" />
                              Pass
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleTestStatusChange(test, 'failed')}
                              disabled={updatingStatusId === test._id}
                            >
                              <FontAwesomeIcon icon={faXmark} className="me-1" />
                              Fail
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="deploy-test-card-empty">
          {tests.length ? 'No tests match these filters' : 'No deploy tests yet'}
        </div>
      )}

      {libraryManageKind && (
        <div className="beta-leave-modal" role="dialog" aria-modal="true" aria-labelledby="deploy-test-library-title">
          <div className="beta-leave-modal-card beta-join-modal-card" style={{ maxWidth: 900 }}>
            <h2 id="deploy-test-library-title">
              Manage {libraryManageKind === 'task' ? 'Tasks' : 'Questions'}
            </h2>
            <p className="text-muted">
              These saved {libraryManageKind === 'task' ? 'tasks' : 'questions'} are only available for {program.track} tests.
            </p>

            {libraryError && (
              <div className="alert alert-danger py-2" role="alert">
                {libraryError}
              </div>
            )}

            {libraryLoading ? (
              <div className="d-flex justify-content-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : libraryItems.length === 0 ? (
              <div className="text-muted py-3">
                No saved {libraryManageKind === 'task' ? 'tasks' : 'questions'} yet.
              </div>
            ) : (
              <div className="d-flex flex-column gap-3 mt-3" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {libraryItems.map((item) => (
                  <div key={item._id} className="border rounded p-3 bg-light">
                    {item.itemType === 'task' ? (
                      <>
                        <label className="form-label fw-bold">Task</label>
                        <input
                          type="text"
                          className="form-control mb-2"
                          value={item.text || ''}
                          onChange={(e) => updateLibraryDraft(item._id, { text: e.target.value })}
                          placeholder="Task title"
                        />
                        <label className="form-label fw-bold">Description</label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={item.description || ''}
                          onChange={(e) => updateLibraryDraft(item._id, { description: e.target.value })}
                          placeholder="Task description"
                        />
                      </>
                    ) : (
                      <>
                        <div className="d-flex gap-2 mb-2">
                          <div className="flex-grow-1">
                            <label className="form-label fw-bold">Question</label>
                            <input
                              type="text"
                              className="form-control"
                              value={item.prompt || ''}
                              onChange={(e) => updateLibraryDraft(item._id, { prompt: e.target.value })}
                              placeholder="Question prompt"
                            />
                          </div>
                          <div style={{ width: 180 }}>
                            <label className="form-label fw-bold">Type</label>
                            <select
                              className="form-select"
                              value={item.type || 'text'}
                              onChange={(e) => updateLibraryDraft(item._id, {
                                type: e.target.value as DeployTestQuestionType,
                                choices: e.target.value === 'choice' ? (item.choices?.length ? item.choices : ['']) : [],
                              })}
                            >
                              <option value="text">Text</option>
                              <option value="boolean">Yes / No</option>
                              <option value="choice">Multiple choice</option>
                            </select>
                          </div>
                        </div>
                        {item.type === 'choice' && (
                          <>
                            <label className="form-label fw-bold">Choices</label>
                            <textarea
                              className="form-control mb-2"
                              rows={3}
                              value={(item.choices || []).join('\n')}
                              onChange={(e) => updateLibraryDraft(item._id, {
                                choices: e.target.value.split('\n').map((choice) => choice.trim()).filter(Boolean),
                              })}
                              placeholder="One choice per line"
                            />
                          </>
                        )}
                        {item.type !== 'text' && (
                          <>
                            <label className="form-label fw-bold">Additional Info</label>
                            <textarea
                              className="form-control"
                              rows={2}
                              value={item.additionalInfo || ''}
                              onChange={(e) => updateLibraryDraft(item._id, { additionalInfo: e.target.value })}
                              placeholder="Optional extra prompt"
                            />
                          </>
                        )}
                      </>
                    )}

                    <div className="d-flex justify-content-end gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => deleteLibraryItem(item)}
                        disabled={deletingLibraryItemId === item._id || savingLibraryItemId === item._id}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-1" />
                        {deletingLibraryItemId === item._id ? 'Deleting...' : 'Delete'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => saveLibraryItem(item)}
                        disabled={savingLibraryItemId === item._id || deletingLibraryItemId === item._id}
                      >
                        {savingLibraryItemId === item._id ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="beta-leave-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeLibraryManager}
                disabled={Boolean(savingLibraryItemId || deletingLibraryItemId)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {startEmailTest && startEmailPreview && (
        <div className="beta-leave-modal" role="dialog" aria-modal="true" aria-labelledby="start-test-email-preview-title">
          <div className="beta-leave-modal-card beta-join-modal-card">
            <h2 id="start-test-email-preview-title">Start Test</h2>
            <p className="text-muted">
              Review the kickoff email before starting <strong>{startEmailTest.name}</strong>.
            </p>

            <div className="kickoff-email-preview start-test-email-preview-box mt-3">
              <p>
                <strong>Subject:</strong>{' '}
                {startEmailPreview.subjectPrefix} (<em>{startEmailPreview.testName}</em>)
              </p>
              <hr />
              <p>Hello testers!</p>
              <div
                className="kickoff-email-preview-html"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(startEmailPreview.body) }}
              />

              <p className="kickoff-email-preview-meta">
                <span><strong>Test Name:</strong> {startEmailPreview.testName}</span>
                <span><strong>Firmware Version:</strong> {startEmailPreview.firmwareVersion}</span>
                <span><strong>Mobile App (iOS):</strong> {startEmailPreview.mobileAppVersionIos}</span>
                <span><strong>Mobile App (Android):</strong> {startEmailPreview.mobileAppVersionAndroid}</span>
              </p>

              <p className="kickoff-email-preview-heading"><strong>Release Notes</strong></p>
              {startEmailPreview.releaseNotes.length ? (
                <ul>
                  {startEmailPreview.releaseNotes.map((note, index) => (
                    <li key={`${note}-${index}`}>{note}</li>
                  ))}
                </ul>
              ) : (
                <p>[Release notes]</p>
              )}

              <p className="kickoff-email-preview-heading"><strong>Instructions</strong></p>
              <div
                className="kickoff-email-preview-html"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(startEmailPreview.instructions) }}
              />

              <p className="kickoff-email-preview-heading"><strong>Tasks</strong></p>
              {startEmailPreview.tasks.length ? (
                <ol>
                  {startEmailPreview.tasks.map((task, index) => (
                    <li key={`${task.title}-${index}`}>
                      <strong>{task.title}</strong>
                      {task.description && (
                        <div className="mt-1">{task.description}</div>
                      )}
                    </li>
                  ))}
                </ol>
              ) : (
                <p>[Tasks]</p>
              )}

              <p className="kickoff-email-preview-heading"><strong>Getting Started</strong></p>
              <p>
                Confirm you are running the correct versions listed above. Make sure you are logged in on the app.
                Then either follow the link in the app to the test or follow the link here.
              </p>

              <p className="kickoff-email-preview-heading"><strong>Need Help?</strong></p>
              <p>
                Check out the{' '}
                <a href={startEmailPreview.wikiUrl} target="_blank" rel="noopener noreferrer">wiki</a>.
              </p>

              <p className="mb-0">
                Happy testing,<br />
                <span className="kickoff-email-preview-signature">~the Fluora team~</span>
              </p>
            </div>

            <div className="text-muted small mt-3">
              Sending will deliver this kickoff email to {defaultKickoffAudienceLabel}.
            </div>

            {startModalError && (
              <div className="alert alert-danger py-2 mt-3 mb-0">
                {startModalError}
              </div>
            )}

            <div className="beta-leave-modal-actions start-test-modal-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={closeStartTestModal}
                disabled={Boolean(startAction)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => startTestFromModal(false)}
                disabled={Boolean(startAction)}
              >
                {startAction === 'without-email' ? 'Starting...' : 'Start Test but DO NOT send email'}
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={() => startTestFromModal(true)}
                disabled={Boolean(startAction)}
              >
                {startAction === 'with-email' ? 'Starting and sending...' : 'Start Test and Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BetaTestingTab;
